import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, MessageCircle } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useSettings } from "../contexts/SettingsContext";

const SLOTS = ["Matin (9h30 – 12h30)", "Après-midi (14h – 17h)", "Fin de journée (17h – 19h)"];

export default function Appointment() {
  const { waLink, settings } = useSettings();
  const [form, setForm] = useState({ name: "", phone: "", day: "", slot: "", reason: "" });
  const [loading, setLoading] = useState(false);
  const [reference, setReference] = useState(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await api.post("/appointments", form);
      setReference(r.data.reference);
      toast.success("Demande de rendez-vous envoyée !");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  if (reference) {
    const hasWhatsApp = Boolean((settings?.whatsapp_number || "").trim());
    return (
      <div className="container-oxy max-w-xl py-12" data-testid="appointment-confirmation">
        <div className="card-oxy p-6 text-center sm:p-10">
          <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100">
            <CheckCircle2 className="h-9 w-9 text-emerald-600" />
          </span>
          <h1 className="mt-5 font-display text-2xl font-extrabold text-slate-900">Demande envoyée</h1>
          <p className="mt-2 text-sm text-slate-600">
            Référence <span className="font-mono font-bold text-gold-dark" data-testid="appointment-reference">{reference}</span><br />
            Nous vous confirmons le créneau par téléphone ou WhatsApp très vite.
          </p>
          <p className="mt-4 text-sm text-slate-600">{form.day} · {form.slot}</p>
          {hasWhatsApp && (
            <a href={waLink(`Bonjour Oxyservices, je confirme ma demande de RDV ${reference} (${form.day}, ${form.slot}).`)}
              target="_blank" rel="noopener noreferrer" data-testid="appointment-whatsapp-btn" className="btn-gold mt-6 w-full">
              <MessageCircle className="h-4 w-4" /> Confirmer sur WhatsApp
            </a>
          )}
          <Link to="/" className="btn-outline-oxy mt-3 w-full">Retour à l'accueil</Link>
        </div>
      </div>
    );
  }

  const minDate = new Date().toISOString().split("T")[0];

  return (
    <div className="container-oxy max-w-xl py-10" data-testid="appointment-page">
      <p className="eyebrow">Rendez-vous</p>
      <h1 className="mt-1 font-display text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">Prendre rendez-vous</h1>
      <p className="mt-2 text-sm text-slate-600">Indiquez vos préférences : nous confirmons le créneau personnellement.</p>

      <form onSubmit={submit} className="card-oxy mt-6 space-y-5 p-5 sm:p-8">
        <div>
          <label className="label-oxy" htmlFor="rdv-name">Nom</label>
          <input id="rdv-name" data-testid="rdv-name-input" required maxLength={80} value={form.name} onChange={set("name")}
            placeholder="Votre nom" className="input-oxy" />
        </div>
        <div>
          <label className="label-oxy" htmlFor="rdv-phone">Téléphone</label>
          <input id="rdv-phone" data-testid="rdv-phone-input" required type="tel" maxLength={20} value={form.phone} onChange={set("phone")}
            placeholder="06 12 34 56 78" className="input-oxy" />
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="label-oxy" htmlFor="rdv-day">Jour souhaité</label>
            <input id="rdv-day" data-testid="rdv-day-input" required type="date" min={minDate} value={form.day} onChange={set("day")} className="input-oxy" />
          </div>
          <div>
            <label className="label-oxy" htmlFor="rdv-slot">Créneau souhaité</label>
            <select id="rdv-slot" data-testid="rdv-slot-select" required value={form.slot} onChange={set("slot")} className="input-oxy">
              <option value="">Choisir…</option>
              {SLOTS.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        </div>
        <div>
          <label className="label-oxy" htmlFor="rdv-reason">Motif <span className="font-normal text-slate-400">(optionnel)</span></label>
          <textarea id="rdv-reason" data-testid="rdv-reason-input" rows={3} value={form.reason} onChange={set("reason")}
            placeholder="Ex : réparation écran, conseil achat…" className="input-oxy resize-none" />
        </div>
        <button type="submit" data-testid="rdv-submit-btn" disabled={loading} className="btn-gold w-full disabled:opacity-60">
          {loading ? "Envoi…" : "Envoyer ma demande"}
        </button>
      </form>
    </div>
  );
}
